<template>
  <NuxtLayout name="blank">
    <div class="error-page">
      <section class="error-page__section">
        <span class="error-page__error-code">Error: {{ statusCode }}</span>

        <BText variant="h1">
          {{ errorTitle }}
        </BText>

        <div class="error-page__text">
          <BText size="sm">
            {{ errorMessage }}
          </BText>
        </div>

        <PButton @click="redirectAction">
          {{ buttonText }}
        </PButton>
      </section>
    </div>
  </NuxtLayout>
</template>

<script setup>
import BText from '~/components/shared/BText.vue';
const props = defineProps({
  error: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();
const router = useRouter();
const { state } = useAuth();

const statusCode = computed(() => Number(props.error?.statusCode) || 500);

// Errors thrown from API responses can carry a structured payload either on the
// NuxtError `data` field or, for legacy axios errors, on `response.data`.
const apiError = computed(() => props.error?.data || props.error?.response?.data || {});

const shouldRedirectToHomepage = computed(() => !!route.redirectedFrom);

const errorComesFromAPI = computed(() => {
  const clientApps = state.value?.user?.profile?.client_apps || [];
  const baseURL = props.error?.config?.baseURL || props.error?.response?.config?.baseURL;
  if (!baseURL) {
    return '';
  }
  const app = clientApps.find((clientApp) => baseURL.includes(clientApp.api_url));
  return app?.text ? `in ${app.text}` : '';
});

const errorTitle = computed(() => {
  if (apiError.value.error) {
    return apiError.value.error;
  }

  switch (statusCode.value) {
    case 404:
      return 'Page not found';
    case 403:
      return 'Forbidden';
    case 408:
      return 'Request Timeout';
    case 500:
      return 'Internal Server Error';
    case 503:
      return 'Service Unavailable';
    case 504:
      return 'Gateway Timeout';
    default:
      return 'An Error Ocurred';
  }
});

const errorMessage = computed(() => {
  if (apiError.value.message) {
    return `${apiError.value.message} ${errorComesFromAPI.value}`.trim();
  }

  switch (statusCode.value) {
    case 404:
      return "We couldn't find this page. You can always find the latest info on our homepage.";
    case 403:
      return 'You do not have permissions to view this page. You can always find the latest info on our homepage.';
    case 408:
      return "We're unable to load this page. Please check your network connection and reload the page.";
    case 500:
      return "We're unable to load this page right now, please contact Apptegy support if this error persists.";
    case 503:
      return "We're unable to load this page right now, please contact Apptegy support if this error persists.";
    case 504:
      return "We're unable to load this page. Please check your network connection and reload the page.";
    default:
      return 'Something went wrong...';
  }
});

const buttonText = computed(() => (shouldRedirectToHomepage.value ? 'Go home' : 'Go back'));

function redirectAction() {
  if (shouldRedirectToHomepage.value) {
    clearError({ redirect: '/' });
  } else {
    clearError();
    router.back();
  }
}
</script>

<style lang="scss" scoped>
.error-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 120px;

  &__error-code {
    color: $color-grey-50;
  }

  &__text {
    margin: 28px 0 32px;
    max-width: 360px;
  }
}
</style>
